import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useCurrentProject } from './current-project.provider'
import { useSession } from './auth.provider'

const STORAGE_KEY = 'TIMEZONE_PREFERENCE'

type TTimezonePreferenceContext = {
  isProjectTimezoneSelected: boolean
  displayTimezone: string | undefined
  alternateTimezone: string
  hasTimezoneMismatch: boolean
  projectTimezone: string | undefined
  browserTimezone: string
  toggleTimezone: () => void
}

const TimezonePreferenceContext = createContext<TTimezonePreferenceContext>({} as TTimezonePreferenceContext)

export const useTimezonePreference = () => useContext(TimezonePreferenceContext)

export const TimezonePreferenceProvider = ({ children }: { children: ReactNode }) => {
  const { project } = useCurrentProject()
  const { currentUser } = useSession()

  const projectTimezone = project.timezone
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const hasTimezoneMismatch =
    !!currentUser?.isAdminOrSales && !!projectTimezone && projectTimezone !== browserTimezone

  const [isProjectTimezoneSelected, setIsProjectTimezoneSelected] = useState(false)

  useEffect(() => {
    if (!hasTimezoneMismatch) {
      setIsProjectTimezoneSelected(false)
      return
    }
    try {
      setIsProjectTimezoneSelected(localStorage.getItem(STORAGE_KEY) !== 'browser')
    } catch {
      setIsProjectTimezoneSelected(false)
    }
  }, [hasTimezoneMismatch])

  const toggleTimezone = useCallback(() => {
    setIsProjectTimezoneSelected((prev) => {
      const next = !prev
      try {
        localStorage.setItem(STORAGE_KEY, next ? 'project' : 'browser')
      } catch {
        // localStorage unavailable
      }
      return next
    })
  }, [])

  const displayTimezone = hasTimezoneMismatch && isProjectTimezoneSelected ? projectTimezone : undefined
  const alternateTimezone = isProjectTimezoneSelected ? browserTimezone : (projectTimezone ?? browserTimezone)

  const value = useMemo<TTimezonePreferenceContext>(
    () => ({
      isProjectTimezoneSelected,
      displayTimezone,
      alternateTimezone,
      hasTimezoneMismatch,
      projectTimezone,
      browserTimezone,
      toggleTimezone,
    }),
    [
      isProjectTimezoneSelected,
      displayTimezone,
      alternateTimezone,
      hasTimezoneMismatch,
      projectTimezone,
      browserTimezone,
      toggleTimezone,
    ],
  )

  return <TimezonePreferenceContext.Provider value={value}>{children}</TimezonePreferenceContext.Provider>
}
